import {Alert, Button, Center, Group, Loader, Modal, Stack, Table, Text} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import React from 'react';

import {ActiveAntibodyFieldDataMigration} from 'antibodies/schema-configuration/AntibodyEnforcedDataModelConstants';
import {AntibodyComponentMigrationConfirmRollbackModal} from 'antibodies/schema-configuration/schema-migration/AntibodyComponentMigrationConfirmRollbackModal';
import {AntibodyFieldMigrationStatusTag} from 'antibodies/schema-configuration/schema-migration/AntibodyFieldMigrationStatusTag';
import {assert} from 'Asserts';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {NumItemsInTagSchemaQuery} from 'schemas/fields/field-migration/__generated/NumItemsInTagSchemaQuery';
import {NUM_ITEMS_IN_TAG_SCHEMA_QUERY} from 'schemas/fields/field-migration/TagSchemaFieldMigrationCreationModal';

const NO_VALUE = '—';

function toPercentStr(numerator: number, total: number): string {
  if (total <= 0) {
    return NO_VALUE;
  }
  return `${Math.round((numerator / total) * 100)}% (${numerator})`;
}

interface FieldMigrationPercentages {
  migrated: string;
  empty: string;
}

/**
 * Computes the migrated / empty percentages for a field, each relative to the total number of items
 * in the schema (so the two sum to 100%):
 *   - migrated: items populated on the destination field after migration
 *   - empty:    items with no value on the destination field (total items − migrated values). This
 *               covers both items that never had a source value and source values explicitly mapped
 *               to null, since neither leaves a value on the destination.
 * Returns dashes when the field's value counts or the schema item total aren't available.
 */
function getFieldPercentages(
  migration: ActiveAntibodyFieldDataMigration,
  numItems: number | null
): FieldMigrationPercentages {
  const {numMigratedFieldValues} = migration;
  if (numMigratedFieldValues == null || numItems == null) {
    return {migrated: NO_VALUE, empty: NO_VALUE};
  }
  return {
    migrated: toPercentStr(numMigratedFieldValues, numItems),
    empty: toPercentStr(numItems - numMigratedFieldValues, numItems),
  };
}

export interface AntibodyComponentMigrationReviewModalProps {
  schemaId: string;
  fieldDataMigrations: ReadonlyArray<ActiveAntibodyFieldDataMigration>;
  onClose: () => void;
  onPublish: () => void | Promise<void>;
  onRollback: () => void | Promise<void>;
  isPublishing?: boolean;
  isRollingBack?: boolean;
}

export function AntibodyComponentMigrationReviewModal({
  schemaId,
  fieldDataMigrations,
  onClose,
  onPublish,
  onRollback,
  isPublishing = false,
  isRollingBack = false,
}: AntibodyComponentMigrationReviewModalProps): JSX.Element {
  const isBusy = isPublishing || isRollingBack;
  const [confirmRollbackOpened, {open: openConfirmRollback, close: closeConfirmRollback}] =
    useDisclosure(false);
  const numItemsResult = useBenchlingQuery<NumItemsInTagSchemaQuery>(NUM_ITEMS_IN_TAG_SCHEMA_QUERY, {
    variables: {id: schemaId},
  });

  let numItems: number | null = null;
  if (numItemsResult.type === QueryResultType.SUCCESS) {
    assert(
      !isInaccessible(numItemsResult.data.tagSchema),
      `Expected to fetch a TagSchema, but it was inaccessible (tagSchemaId=${schemaId})`
    );
    numItems = numItemsResult.data.tagSchema?.numItems ?? null;
  }

  return (
    <Modal
      title="Review & publish"
      opened={true}
      onClose={onClose}
      size="lg"
      padding="lg"
      // While the rollback confirmation is open, don't let Escape (or an outside click) also reach this
      // modal — Mantine dispatches those to every open modal, so an unguarded parent would close the
      // whole review flow instead of just dismissing the confirmation.
      closeOnClickOutside={!isBusy && !confirmRollbackOpened}
      closeOnEscape={!isBusy && !confirmRollbackOpened}
      withCloseButton={!isBusy}
      closeButtonProps={{'aria-label': 'Close'}}
    >
      {numItemsResult.type === QueryResultType.PENDING ? (
        <Center>
          <Loader />
        </Center>
      ) : (
        <Stack spacing="lg">
          {numItemsResult.type === QueryResultType.ERROR && (
            <Alert variant="warning">
              Couldn't load item counts for this schema, so migration percentages are unavailable.
            </Alert>
          )}
          <Text>Review the status of each migrated field before publishing.</Text>
          <Table>
            <thead>
              <tr>
                <th>Field</th>
                <th>Migrated from</th>
                <th>Status</th>
                <th>Values migrated</th>
                <th>Values empty</th>
              </tr>
            </thead>
            <tbody>
              {fieldDataMigrations.map((migration) => {
                const percentages = getFieldPercentages(migration, numItems);
                return (
                  <tr key={migration.destinationFieldApiId}>
                    <td>{migration.destinationFieldName}</td>
                    <td>{migration.sourceFieldName}</td>
                    <td>
                      <AntibodyFieldMigrationStatusTag status={migration.status} />
                    </td>
                    <td>{percentages.migrated}</td>
                    <td>{percentages.empty}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <Group position="apart">
            <Button
              variant="warn"
              onClick={openConfirmRollback}
              loading={isRollingBack}
              disabled={isPublishing}
            >
              Rollback migration
            </Button>
            <Group spacing="sm">
              <Button variant="secondary" onClick={onClose} disabled={isBusy}>
                Cancel
              </Button>
              <Button
                onClick={onPublish}
                loading={isPublishing}
                disabled={isRollingBack}
                data-autofocus={true}
              >
                Publish migration
              </Button>
            </Group>
          </Group>
        </Stack>
      )}
      {confirmRollbackOpened && (
        <AntibodyComponentMigrationConfirmRollbackModal
          onConfirm={onRollback}
          onClose={closeConfirmRollback}
          isRollingBack={isRollingBack}
        />
      )}
    </Modal>
  );
}
